/**
 * DataMigrationPage.tsx
 * Admin-only page for uploading SPP Excel workbooks for historical data migration.
 * Upload pattern mirrors ConfigUploadPanel exactly.
 */

import { useState, useEffect, useCallback } from 'react';
import { Upload, Progress, Alert, Card, Button, Table, Tag, Tooltip } from 'antd';
import {
  UploadOutlined,
  ReloadOutlined,
  CloseOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import { NSPButton } from '@components/ui/NSPButton';
import {
  useMigrationUpload,
  type MigrationHistoryItem,
  type MigrationSkippedBreakdown,
} from '@hooks/useMigrationUpload';
import styles from './DataMigrationPage.module.css';

// ── Progress helpers ─────────────────────────────────────────────────────────

type ProgressStatus = 'idle' | 'active' | 'completed' | 'failed';

function toAntdStatus(s: ProgressStatus): 'active' | 'success' | 'exception' | 'normal' {
  if (s === 'active') return 'active';
  if (s === 'completed') return 'success';
  if (s === 'failed') return 'exception';
  return 'normal';
}

function statusClass(status: string): string {
  if (status === 'Completed') return styles.statusCompleted;
  if (status === 'CompletedWithErrors') return styles.statusWithErrors;
  if (status === 'Failed') return styles.statusFailed;
  if (status === 'Processing') return styles.statusProcessing;
  return styles.statusDefault;
}

// ── History table columns ────────────────────────────────────────────────────

const historyColumns: ColumnsType<MigrationHistoryItem> = [
  {
    title: 'File',
    dataIndex: 'file_name',
    ellipsis: true,
    render: (name: string) => (
      <Tooltip title={name}>
        <span>{name}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Uploaded',
    dataIndex: 'uploaded_at',
    width: 170,
    render: (v: string) => new Date(v).toLocaleString(),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    width: 160,
    render: (s: string) => (
      <span className={`${styles.statusBadge} ${statusClass(s)}`}>{s}</span>
    ),
  },
  {
    title: 'Records Inserted',
    dataIndex: 'valid_rows_inserted',
    width: 140,
    align: 'right',
  },
  {
    title: 'Errors',
    dataIndex: 'invalid_rows_skipped',
    width: 80,
    align: 'right',
    render: (n: number, row: MigrationHistoryItem) =>
      row.has_errors ? <Tag color="warning">{n}</Tag> : <span>{n}</span>,
  },
];

// ── Skipped-sheets breakdown block ───────────────────────────────────────────

const SKIP_LABELS: Record<keyof MigrationSkippedBreakdown, string> = {
  hidden:              'Hidden',
  accrual:             'Accrual',
  emptyOrUnrecognised: 'Empty or unrecognised',
  readError:           'Read error',
  parseError:          'Parse error',
};

function SkippedSheetsBlock({ total, breakdown }: { total: number; breakdown: MigrationSkippedBreakdown }) {
  const rows = (Object.keys(SKIP_LABELS) as (keyof MigrationSkippedBreakdown)[])
    .filter((key) => breakdown[key].length > 0);

  return (
    <div className={styles.skippedBlock}>
      <div className={styles.skippedHeader}>
        {total} sheet{total !== 1 ? 's' : ''} skipped
      </div>
      <ul className={styles.skippedList}>
        {rows.map((key) => (
          <li key={key} className={styles.skippedItem}>
            <Tooltip title={breakdown[key].join(', ')} placement="right">
              <span className={styles.skippedCount}>{breakdown[key].length}</span>
              <span className={styles.skippedLabel}>{SKIP_LABELS[key]}</span>
            </Tooltip>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function DataMigrationPage() {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>('idle');
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [history, setHistory] = useState<MigrationHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const { uploading, result, error, uploadFile, downloadErrorReport, fetchHistory, reset } =
    useMigrationUpload();

  // Load migration history on mount
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const data = await fetchHistory(1, 15);
    if (data) setHistory(data.items);
    setHistoryLoading(false);
  }, [fetchHistory]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // ── File list handler ──────────────────────────────────────────────
  const handleChange = ({ fileList: newList }: { fileList: UploadFile[] }) => {
    setFileList(newList.slice(-1)); // keep only 1 file
    if (result || error) reset();
  };

  const handleCloseProgress = () => {
    setProgressStatus('idle');
    setProgressPercent(0);
    setProgressMessage(null);
  };

  // ── Upload handler ─────────────────────────────────────────────────
  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj as File | undefined;
    if (!file) return;

    setProgressStatus('active');
    setProgressPercent(20);
    setProgressMessage('Uploading file...');

    try {
      setProgressPercent(50);
      setProgressMessage('Processing sheets...');

      const ok = await uploadFile(file);

      if (ok) {
        setProgressStatus('completed');
        setProgressPercent(100);
        setProgressMessage('Migration completed');
        setFileList([]);
        loadHistory(); // refresh history
      } else {
        setProgressStatus('failed');
        setProgressPercent(100);
        setProgressMessage('Migration failed');
      }
    } catch {
      setProgressStatus('failed');
      setProgressPercent(100);
      setProgressMessage('Migration failed');
    }
  };

  // ── Derived state ──────────────────────────────────────────────────
  const isBusy = uploading;
  const showProgress = progressStatus !== 'idle';
  const canClose = progressStatus === 'completed' || progressStatus === 'failed';
  const uploadResult = result?.success ? result.data : null;

  return (
    <div className={styles.page}>
      {/* ── Page heading ──────────────────────────────────────────── */}
      <div className={styles.pageTitle}>SPP Data Migration</div>
      <div className={styles.pageSubtitle}>
        Upload a historical SPP Excel workbook (.xlsx / .xls / .xlsm) to import vendor
        and consolidated meter data into staging tables.
      </div>

      {/* ── Upload panel ──────────────────────────────────────────── */}
      <div className={styles.uploadPanel}>
        <div className={styles.panelTitle}>Upload Migration File</div>

        {/* Error alert */}
        {error && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={reset}
            className={styles.alert}
            message="Upload Failed"
            description={error}
          />
        )}

        {/* Success alert */}
        {uploadResult && (
          <Alert
            type="success"
            showIcon
            closable
            onClose={reset}
            className={styles.alert}
            message="Migration Completed"
            description={
              <span>
                <strong>{(uploadResult.stats.validRowsInserted ?? 0).toLocaleString()}</strong> records
                inserted across{' '}
                <strong>{uploadResult.stats.sheetsProcessed}</strong> sheets.
                {uploadResult.stats.errorCount > 0 && (
                  <span>
                    {' '}
                    <strong style={{ color: '#dc2626' }}>
                      {uploadResult.stats.errorCount}
                    </strong>{' '}
                    row-level errors encountered.
                  </span>
                )}
              </span>
            }
          />
        )}

        {/* Select File button */}
        <Upload
          accept=".xlsx,.xls,.xlsm"
          fileList={fileList}
          beforeUpload={() => false}
          onChange={handleChange}
          maxCount={1}
          onRemove={() => {
            setFileList([]);
            if (result || error) reset();
          }}
          showUploadList={{ showRemoveIcon: true, showPreviewIcon: false }}
        >
          <NSPButton icon={<UploadOutlined />} disabled={isBusy}>
            Select File
          </NSPButton>
        </Upload>

        {/* Progress bar */}
        {showProgress && (
          <Card
            size="small"
            className={styles.progressCard}
            extra={
              canClose && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleCloseProgress}
                />
              )
            }
          >
            <Progress
              percent={progressPercent}
              status={toAntdStatus(progressStatus)}
              strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
            />
            <div className={styles.progressMessage}>{progressMessage}</div>
          </Card>
        )}

        {/* Action bar: Upload (left) | Refresh (right) */}
        <div className={styles.actionBar}>
          <NSPButton
            onClick={handleUpload}
            disabled={fileList.length === 0 || isBusy}
            loading={isBusy}
          >
            {isBusy ? 'Migrating...' : 'Upload & Migrate'}
          </NSPButton>

          <NSPButton icon={<ReloadOutlined />} onClick={loadHistory}>
            Refresh
          </NSPButton>
        </div>
      </div>

      {/* ── Stats card (shown after a successful upload) ───────────── */}
      {uploadResult && (
        <Card
          size="small"
          className={styles.statsCard}
          title={<span className={styles.statsTitle}>Migration Results</span>}
          extra={
            uploadResult.stats.errorCount > 0 && (
              <NSPButton
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadErrorReport(
                    uploadResult.migrationBatchId,
                    fileList[0]?.name ?? 'report'
                  )
                }
              >
                Download Error Report
              </NSPButton>
            )
          }
        >
          <div className={styles.batchId}>
            Batch ID: <strong>{uploadResult.migrationBatchId}</strong>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <div className={styles.statLabel}>Sheets Processed</div>
              <div className={styles.statValue}>
                {uploadResult.stats.sheetsProcessed} / {uploadResult.stats.totalSheets}
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statLabel}>Rows Read</div>
              <div className={styles.statValue}>
                {(uploadResult.stats.totalRowsRead ?? 0).toLocaleString()}
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statLabel}>Records Inserted</div>
              <div className={styles.statValue}>
                {(uploadResult.stats.validRowsInserted ?? 0).toLocaleString()}
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statLabel}>Consolidated Records</div>
              <div className={styles.statValue}>
                {(uploadResult.stats.consolidatedRecordsInserted ?? 0).toLocaleString()}
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statLabel}>Row Errors</div>
              <div
                className={
                  uploadResult.stats.errorCount > 0
                    ? styles.statValueError
                    : styles.statValue
                }
              >
                {(uploadResult.stats.errorCount ?? 0).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Skipped sheets block — only shown when at least 1 sheet was skipped */}
          {uploadResult.stats.sheetsSkipped > 0 && (
            <SkippedSheetsBlock
              total={uploadResult.stats.sheetsSkipped}
              breakdown={uploadResult.stats.sheetsSkippedBreakdown}
            />
          )}
        </Card>
      )}

      {/* ── History table ──────────────────────────────────────────── */}
      <div className={styles.historySection}>
        <div className={styles.historySectionTitle}>Recent Migrations</div>
        <Table<MigrationHistoryItem>
          dataSource={history}
          columns={historyColumns}
          rowKey="id"
          loading={historyLoading}
          size="small"
          pagination={{ pageSize: 10, size: 'small' }}
          locale={{ emptyText: 'No migrations yet' }}
          expandable={{
            rowExpandable: (row) => row.has_errors,
            expandedRowRender: (row) => (
              <div style={{ padding: '4px 8px' }}>
                <NSPButton
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    downloadErrorReport(row.migration_batch_id, row.file_name)
                  }
                >
                  Download Error Report
                </NSPButton>
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
}
